import type { ColDef, IRowNode } from '@mint-lib/ag-grid/community';

import {
  USER_COLUMN_ID,
  type UserColumnConfig,
} from '../../api/queries/userColumnsConfig.js';
import TextCell from '../../atoms/TextCell/index.js';
import EmailCell from './components/EmailCell.js';
import LabelCell from './components/LabelCell.js';
import NameCell, { getUserFullName } from './components/NameCell.js';
import RoleCell from './components/RoleCell.js';
import StatusCell from './components/StatusCell.js';
import type { UserTableV2Row } from './datasource.js';

type ProvideUserColumnFieldsOptions = {
  onViewAllLabels?: (user: UserTableV2Row) => void;
  onRoleChange?: (
    user: UserTableV2Row,
    newRole: string | number,
    node: IRowNode<UserTableV2Row>,
  ) => void;
};

export const provideUserColumnFields = (
  configList: UserColumnConfig[],
  options: ProvideUserColumnFieldsOptions = {},
): ColDef<UserTableV2Row>[] =>
  configList.map((config) => {
    const base: ColDef<UserTableV2Row> = {
      colId: config.id,
      headerName: config.label,
      hide: config.isHidden,
      lockVisible: config.isMandatory,
    };

    if (config.id === USER_COLUMN_ID.fullName) {
      return {
        ...base,
        flex: 1,
        minWidth: 160,
        cellRenderer: NameCell,
        valueGetter: ({ data }) => getUserFullName(data),
      };
    }

    if (config.id === USER_COLUMN_ID.email) {
      return {
        ...base,
        flex: 1.5,
        minWidth: 200,
        field: 'email',
        cellRenderer: EmailCell,
      };
    }

    if (config.id === USER_COLUMN_ID.role) {
      return {
        ...base,
        flex: 1,
        minWidth: 160,
        field: 'role',
        cellRenderer: RoleCell,
        cellRendererParams: {
          options: config.options ?? [],
          onRoleChange: options.onRoleChange,
        },
      };
    }

    if (config.id === USER_COLUMN_ID.status) {
      return {
        ...base,
        width: 140,
        maxWidth: 200,
        cellRenderer: StatusCell,
        valueGetter: ({ data }) => data?.status,
      };
    }

    if (config.id === USER_COLUMN_ID.labels) {
      return {
        ...base,
        flex: 1.5,
        minWidth: 260,
        cellRenderer: LabelCell,
        cellRendererParams: {
          onViewAllLabels: options.onViewAllLabels,
        },
        valueGetter: ({ data }) => data?.tags,
      };
    }

    return {
      ...base,
      cellRenderer: TextCell,
      valueGetter: ({ data }) =>
        data ? (data as Record<string, unknown>)[config.id] : undefined,
    };
  });
